import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  message,
  Typography,
  Tag,
  Modal,
  Form,
  InputNumber,
  Input
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { paymentsAPI, adminAPI } from '../../utils/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [consumers, setConsumers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    startDate: null,
    endDate: null,
    payment_type: undefined,
    status: undefined
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchConsumers();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
        search: filters.search || undefined,
        start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
        end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
        payment_type: filters.payment_type,
        status: filters.status
      };
      const response = await paymentsAPI.getPayments(params);
      setPayments(response.data.payments || []);
      setSummary(response.data.summary);
    } catch (error) {
      message.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchConsumers = async () => {
    try {
      const response = await adminAPI.getConsumers({
        is_active: true,
        limit: 1000
      });
      setConsumers(response.data.consumers || []);
    } catch (error) {
      message.error('Failed to fetch consumers');
    }
  };

  const fetchSummary = async () => {
    try {
      // Pass filtering parameters to summary if supported (backend currently supports payment_type)
      const response = await paymentsAPI.getPaymentSummary({
        payment_type: filters.payment_type
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch payment summary:', error);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      startDate: null,
      endDate: null,
      payment_type: undefined,
      status: undefined
    });
  };

  const handleSubmit = async (values) => {
    try {
      const paymentData = {
        ...values,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
        period_start_date: values.period_start_date.format('YYYY-MM-DD'),
        period_end_date: values.period_end_date.format('YYYY-MM-DD')
      };

      if (editingRecord) {
        await paymentsAPI.updatePayment(editingRecord.id, paymentData);
        message.success('Payment updated successfully');
      } else {
        await paymentsAPI.createPayment(paymentData);
        message.success('Payment recorded successfully');
      }

      setModalVisible(false);
      setEditingRecord(null);
      form.resetFields();
      fetchPayments();
      fetchSummary();
    } catch (error) {
      message.error(`Failed to ${editingRecord ? 'update' : 'record'} payment`);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      payment_date: record.payment_date ? dayjs(record.payment_date) : null,
      period_start_date: record.period_start_date ? dayjs(record.period_start_date) : null,
      period_end_date: record.period_end_date ? dayjs(record.period_end_date) : null
    });
    setModalVisible(true);
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = payments.map(payment => ({
        'ID': payment.center_consumer_id,
        'Consumer Name': payment.consumer_name,
        'Amount': `₹${payment.amount}`,
        'Type': payment.payment_type?.toUpperCase(),
        'Method': payment.payment_method?.toUpperCase(),
        'Payment Date': dayjs(payment.payment_date).format('YYYY-MM-DD'),
        'Period Start': dayjs(payment.period_start_date).format('YYYY-MM-DD'),
        'Period End': dayjs(payment.period_end_date).format('YYYY-MM-DD'),
        'Status': payment.status?.toUpperCase(),
        'Transaction ID': payment.transaction_id || '-',
        'Notes': payment.notes || '-'
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);

      const wscols = [
        { wch: 8 },  // ID
        { wch: 20 }, // Consumer Name
        { wch: 12 }, // Amount
        { wch: 12 }, // Type
        { wch: 12 }, // Method
        { wch: 15 }, // Payment Date
        { wch: 15 }, // Period Start
        { wch: 15 }, // Period End
        { wch: 12 }, // Status
        { wch: 20 }, // Transaction ID
        { wch: 30 }, // Notes
      ];
      worksheet['!cols'] = wscols;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payments');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

      const fileName = `Payments_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
      saveAs(data, fileName);
      message.success('Payments exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export payments');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Payment',
      content: 'Are you sure you want to delete this payment record?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await paymentsAPI.deletePayment(record.id);
          message.success('Payment deleted successfully');
          fetchPayments();
          fetchSummary();
        } catch (error) {
          message.error('Failed to delete payment');
        }
      }
    });
  };

  const showAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      payment_date: dayjs(),
      period_start_date: dayjs(),
      period_end_date: dayjs().add(1, 'month'),
      payment_type: 'monthly',
      status: 'completed'
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'center_consumer_id',
      key: 'center_consumer_id',
      width: 70,
      render: (id) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{id}</span>
    },
    {
      title: 'Consumer',
      dataIndex: 'consumer_name',
      key: 'consumer_name',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `₹${amount}`,
    },
    {
      title: 'Type',
      dataIndex: 'payment_type',
      key: 'payment_type',
      render: (type) => (
        <Tag color={type === 'monthly' ? 'blue' : 'green'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => method || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'completed' ? 'success' :
          status === 'pending' ? 'warning' : 'error';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Payment Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-',
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <span>
          {record.period_start_date ? dayjs(record.period_start_date).format('MMM DD') : '-'} - {record.period_end_date ? dayjs(record.period_end_date).format('MMM DD, YYYY') : '-'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            size="small"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} wrap={false}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Payments Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPayments}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={payments.length === 0}
              style={{ backgroundColor: '#1d6f42', borderColor: '#1d6f42', color: '#fff' }}
            >
              Export Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Record Payment
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Summary Cards */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: 8 }}>
                  {summary.completed_payments || 0}
                </Title>
                <p style={{ margin: 0, color: '#666' }}>Completed Payments</p>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Title level={3} style={{ color: '#52c41a', marginBottom: 8 }}>
                  ₹{summary.total_completed_amount?.toLocaleString() || 0}
                </Title>
                <p style={{ margin: 0, color: '#666' }}>Total Revenue</p>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <Title level={3} style={{ color: '#faad14', marginBottom: 8 }}>
                  {summary.pending_payments || 0}
                </Title>
                <p style={{ margin: 0, color: '#666' }}>Pending Payments</p>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={7}>
            <Input
              placeholder="Search by consumer name..."
              prefix={<SearchOutlined />}
              allowClear
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </Col>
          <Col xs={24} md={7}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null}
              onChange={(dates) => {
                setFilters(prev => ({
                  ...prev,
                  startDate: dates ? dates[0] : null,
                  endDate: dates ? dates[1] : null
                }));
              }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="All Types"
              style={{ width: '100%' }}
              allowClear
              value={filters.payment_type}
              onChange={(val) => setFilters(prev => ({ ...prev, payment_type: val }))}
            >
              <Option value={undefined}>All</Option>
              <Option value="daily">Daily</Option>
              <Option value="monthly">Monthly</Option>
            </Select>
          </Col>
          <Col xs={24} md={3}>
            <Select
              placeholder="All Status"
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(val) => setFilters(prev => ({ ...prev, status: val }))}
            >
              <Option value={undefined}>All</Option>
              <Option value="completed">Completed</Option>
              <Option value="pending">Pending</Option>
              <Option value="failed">Failed</Option>
            </Select>
          </Col>
          <Col xs={24} md={3}>
            <Button
              style={{ width: '100%' }}
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingRecord ? 'Edit Payment' : 'Record Payment'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="consumer_id"
                label="Consumer"
                rules={[{ required: true, message: 'Please select a consumer' }]}
              >
                <Select
                  placeholder="Select consumer"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {consumers.map(consumer => (
                    <Option key={consumer.id} value={consumer.id}>
                      {consumer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="amount"
                label="Amount (₹)"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="Enter amount"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payment_type"
                label="Payment Type"
                rules={[{ required: true, message: 'Please select payment type' }]}
              >
                <Select placeholder="Select payment type">
                  <Option value="daily">Daily</Option>
                  <Option value="monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="payment_method" label="Payment Method">
                <Select placeholder="Select payment method">
                  <Option value="cash">Cash</Option>
                  <Option value="card">Card</Option>
                  <Option value="online">Online</Option>
                  <Option value="bank_transfer">Bank Transfer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="payment_date"
                label="Payment Date"
                rules={[{ required: true, message: 'Please select payment date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="pending">Pending</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="failed">Failed</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="period_start_date"
                label="Period Start Date"
                rules={[{ required: true, message: 'Please select period start date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="period_end_date"
                label="Period End Date"
                rules={[{ required: true, message: 'Please select period end date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="transaction_id" label="Transaction ID">
                <Input placeholder="Optional transaction ID" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRecord ? 'Update' : 'Record'} Payment
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Payments;
